import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { getApiErrorMessage } from '@/shared/api/axios';
import { usePayrollEmployees, usePayrollSetup, useSaveCompensation, type PayrollAccess, type PayrollEmployee } from './api';

const inr = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

function EditSalaryModal({ employee, onClose }: { employee: PayrollEmployee | null; onClose: () => void }) {
  const { data: setup } = usePayrollSetup();
  const save = useSaveCompensation();
  const [salary, setSalary] = useState('');
  const [structure, setStructure] = useState('');
  const [bonus, setBonus] = useState('');
  const [from, setFrom] = useState('');
  const [reason, setReason] = useState('');
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  // Start from the current figures each time a different person is opened.
  if (employee && loadedFor !== employee.id) {
    setLoadedFor(employee.id);
    setSalary(String(employee.base_salary || ''));
    setStructure(employee.structure_code ?? '');
    setBonus(employee.bonus ? String(employee.bonus) : '');
    setFrom(new Date().toISOString().slice(0, 10));
    setReason('');
  }
  if (!employee && loadedFor) setLoadedFor(null);

  const submit = async () => {
    if (!employee) return;
    try {
      await save.mutateAsync({
        user_id: employee.id, base_salary: Number(salary), structure_code: structure || null,
        bonus: bonus ? Number(bonus) : 0, effective_from: from, change_reason: reason,
      });
      toast.success('Salary saved.');
      onClose();
    } catch (err) { toast.error(getApiErrorMessage(err)); }
  };

  return (
    <Modal open={!!employee} onClose={onClose} title={`Salary: ${employee?.display_name ?? ''}`} size="md" footer={
      <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={submit} disabled={save.isPending || !salary}>Save</Button></div>
    }>
      <div className="space-y-3 py-2">
        <Input label="Monthly salary (₹)" type="number" min={0} value={salary} onChange={(e) => setSalary(e.target.value)} />
        <Select
          label="Salary structure"
          value={structure}
          placeholder="Use the Standard structure"
          options={[{ value: '', label: 'Use the Standard structure' }, ...(setup?.structures ?? []).filter((s) => s.active).map((s) => ({ value: s.code, label: s.name }))]}
          onChange={(e) => setStructure(e.target.value)}
        />
        <Input label="Monthly bonus (₹, optional)" type="number" min={0} value={bonus} onChange={(e) => setBonus(e.target.value)} hint="Added to every payslip as a Bonus line." />
        <Input label="Effective from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} hint="A change dated in the future does not affect earlier months." />
        <Input label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Annual review, promotion..." />
      </div>
    </Modal>
  );
}

export function EmployeesTab({ access }: { access: PayrollAccess }) {
  const { data: employees, isLoading } = usePayrollEmployees();
  const [editing, setEditing] = useState<PayrollEmployee | null>(null);
  const [search, setSearch] = useState('');
  const rows = (employees ?? []).filter((e) => !search.trim() || `${e.display_name} ${e.email} ${e.department}`.toLowerCase().includes(search.trim().toLowerCase()));
  const without = (employees ?? []).filter((e) => !(e.base_salary > 0)).length;

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Input placeholder="Search employees" value={search} onChange={(e) => setSearch(e.target.value)} className="w-64" />
        {without > 0 && <p className="text-sm text-amber-700">{without} employee(s) have no salary yet and will be skipped when payslips are generated.</p>}
      </div>
      <div className="max-h-[520px] overflow-y-auto">
        {isLoading ? <p className="p-8 text-center text-on-surface-variant">Loading…</p> : (
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 z-10 bg-surface-variant text-xs uppercase text-on-surface-variant">
              <tr>
                <th className="px-4 py-3 font-medium">Employee</th>
                <th className="px-4 py-3 font-medium">Department</th>
                <th className="px-4 py-3 font-medium">Monthly salary</th>
                <th className="px-4 py-3 font-medium">Structure</th>
                <th className="px-4 py-3 font-medium">Bonus</th>
                {access.canManage && <th className="px-4 py-3 text-right font-medium" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-outline/50">
              {rows.map((e) => (
                <tr key={e.id} className="hover:bg-surface-variant/40">
                  <td className="px-4 py-3"><div className="font-medium text-on-surface">{e.display_name}</div><div className="text-xs text-on-surface-variant">{e.email}</div></td>
                  <td className="px-4 py-3">{e.department}{e.position ? ` · ${e.position}` : ''}</td>
                  <td className="px-4 py-3 font-semibold text-emerald-600">{e.base_salary > 0 ? inr(e.base_salary) : <span className="font-normal text-amber-700">Not set</span>}</td>
                  <td className="px-4 py-3">{e.structure_name ?? <span className="text-on-surface-variant">Standard (default)</span>}</td>
                  <td className="px-4 py-3">{e.bonus ? inr(e.bonus) : '-'}</td>
                  {access.canManage && <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => setEditing(e)}>Edit salary</Button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <EditSalaryModal employee={editing} onClose={() => setEditing(null)} />
    </div>
  );
}
